// screen: Library trash (Studio change trash-purge): restore, delete permanently, empty trash, retention line.
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, RotateCcw, Trash2 } from 'lucide-react';
import type { ApiError } from '@viewstream/api-client';
import { Badge, Button, Dialog, DialogContent, EmptyState, ErrorState, Input, Skeleton, useToast } from '@viewstream/ui';
import { fmtDateTime } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { problemText } from '~/app/team';
import { confirmWordOk, fmtBytes, useDeletePermanently, useEmptyTrash, useRestore, useTrash, type TrashItem } from '~/app/trash';

export const Route = createFileRoute('/_layout/t/$slug/library/trash')({ component: Screen });

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const toast = useToast();
  React.useEffect(() => { document.title = appTitle(t`Trash`); }, [t]);
  const trash = useTrash(tenant.slug);
  const canWrite = can(scopes, 'assets:write');
  const canPurge = can(scopes, 'assets:purge');
  const restore = useRestore(tenant.slug);
  const purge = useDeletePermanently(tenant.slug);
  const empty = useEmptyTrash(tenant.slug);
  const [confirmOne, setConfirmOne] = React.useState<TrashItem | null>(null);
  const [confirmAll, setConfirmAll] = React.useState(false);
  const [typed, setTyped] = React.useState('');
  const fail = (e: unknown) => toast.push({ title: t`Something went wrong`, description: problemText(e), tone: 'danger' });
  const data = trash.data;
  const pendingItems = data?.items.filter((i) => !i.purge_pending) ?? [];
  const untitled = t`(untitled)`;

  return (
    <div>
      <Link to={'/t/$slug/library' as never} params={{ slug: tenant.slug } as never} className="mb-2 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"><ArrowLeft className="size-4 icon-dir" aria-hidden /> {t`Library`}</Link>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t`Trash`}</h1>
        {canPurge && data && pendingItems.length > 0 ? (
          <Button variant="danger" onClick={() => { setTyped(''); setConfirmAll(true); }} data-testid="empty-trash"><Trash2 className="size-4" aria-hidden /> {t`Empty trash`}</Button>
        ) : null}
      </div>
      {data ? (
        <p className="mb-4 max-w-2xl text-sm text-fg-muted" data-testid="trash-retention">
          {t`Items in the trash are deleted permanently after ${data.retention_days} days. A trashed asset keeps its master, so it can be restored until then.`}
          {data.count > 0 ? <> {t`${data.count} items, ${fmtBytes(data.bytes, locale)} in storage.`}</> : null}
        </p>
      ) : null}
      {trash.isPending ? <Skeleton className="h-32 w-full" /> : trash.isError ? (
        <ErrorState error={trash.error as ApiError} onRetry={() => trash.refetch()} labels={{ title: t`Could not load the trash`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState icon={<Trash2 className="size-6" aria-hidden />} title={t`Trash is empty`} />
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border" aria-label={t`Deleted assets`}>
          {data.items.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 p-3 text-sm" data-asset-id={a.id}>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{a.title || t`(untitled)`}</div>
                <div className="text-xs text-fg-muted">
                  {a.deleted_at ? t`Deleted ${fmtDateTime(a.deleted_at, { locale, tz })}` : null}
                  {a.purge_at ? <> · {t`deleted permanently on ${fmtDateTime(a.purge_at, { locale, tz })}`}</> : null}
                  {' · '}{fmtBytes(a.bytes, locale)}
                </div>
              </div>
              {a.purge_pending ? (
                <Badge tone="neutral" data-testid="purge-pending">{t`Deleting permanently…`}</Badge>
              ) : (
                <div className="flex shrink-0 gap-2">
                  {canWrite ? (
                    <Button size="sm" variant="secondary" disabled={!a.restorable || restore.isPending} title={a.restorable ? undefined : t`The master is gone; this asset cannot be restored`}
                      onClick={() => restore.mutate(a.id, { onSuccess: () => toast.push({ title: t`Restoring — the asset is rebuilt from its master`, tone: 'ok' }), onError: fail })}>
                      <RotateCcw className="size-4" aria-hidden /> {t`Restore`}
                    </Button>
                  ) : null}
                  {canPurge ? (
                    <Button size="sm" variant="danger" onClick={() => setConfirmOne(a)}><Trash2 className="size-4" aria-hidden /> {t`Delete permanently`}</Button>
                  ) : null}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!confirmOne} onOpenChange={(o) => { if (!o) setConfirmOne(null); }}>
        {confirmOne ? (
          <DialogContent title={t`Delete "${confirmOne.title || untitled}" permanently?`} description={t`The master, the uploaded source and every copy on the CDN are deleted (${fmtBytes(confirmOne.bytes, locale)}). This cannot be undone.`} closeLabel={t`Close`}>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmOne(null)}>{t`Cancel`}</Button>
              <Button variant="danger" disabled={purge.isPending} onClick={() => purge.mutate(confirmOne.id, {
                onSuccess: () => { toast.push({ title: t`Deleting permanently`, tone: 'ok' }); setConfirmOne(null); },
                onError: fail,
              })}>{t`Delete permanently`}</Button>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>

      <Dialog open={confirmAll} onOpenChange={setConfirmAll}>
        {data ? (
          <DialogContent title={t`Empty the trash?`} description={t`${pendingItems.length} assets and ${fmtBytes(data.bytes, locale)} are deleted permanently: masters, uploaded sources and CDN copies. This cannot be undone.`} closeLabel={t`Close`}>
            <form className="mt-4 space-y-3" onSubmit={(e) => {
              e.preventDefault();
              if (!confirmWordOk(typed, tenant.slug)) return;
              empty.mutate(undefined, {
                onSuccess: (r) => { toast.push({ title: t`${r.queued} assets are being deleted permanently`, tone: 'ok' }); setConfirmAll(false); },
                onError: fail,
              });
            }}>
              <label className="block text-sm" htmlFor="empty-confirm">{t`Type מחק or the tenant name "${tenant.slug}" to confirm`}</label>
              <Input id="empty-confirm" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="empty-confirm" />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setConfirmAll(false)}>{t`Cancel`}</Button>
                <Button type="submit" variant="danger" disabled={!confirmWordOk(typed, tenant.slug) || empty.isPending} data-testid="empty-confirm-submit">{t`Empty trash`}</Button>
              </div>
            </form>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}
